import {
  IEnumOption,
  IWorkflowExecution,
  IWorkflowRuleSummary,
  IWorkflowSchema,
  IWorkflowTemplate,
  WorkflowExecutionStatus,
  WorkflowRuleStatus,
  WorkflowTriggerType,
} from '../models/workflows.models';

export interface IWorkflowMockConfig {
  rulesCount: number;
  historyCount: number;
}

export const WORKFLOW_MOCK_CONFIG: IWorkflowMockConfig = {
  rulesCount: 60,
  historyCount: 240,
};

export const MOCK_WORKFLOW_SCHEMA: IWorkflowSchema = {
  triggers: [
    { type: 'new_email_received', category: 'smart_inbox', i18nLabel: 'workflows.triggers.new_email_received', icon: 'mail', disabled: false, conditionCategories: ['email', 'conversation', 'customer'], actionCategories: ['thread', 'task', 'email'] },
    { type: 'email_categorized', category: 'smart_inbox', i18nLabel: 'workflows.triggers.email_categorized', icon: 'label', disabled: false, conditionCategories: ['email', 'conversation', 'customer'], actionCategories: ['thread', 'task', 'email'] },
    { type: 'task_status_changed', category: 'smart_inbox', i18nLabel: 'workflows.triggers.task_status_changed', icon: 'task_alt', disabled: false, conditionCategories: ['email', 'conversation', 'customer'], actionCategories: ['thread', 'task', 'email'] },
    { type: 'thread_assigned', category: 'smart_inbox', i18nLabel: 'workflows.triggers.thread_assigned', icon: 'person_add', disabled: false, conditionCategories: ['email', 'conversation', 'customer'], actionCategories: ['thread', 'task', 'email'] },
    { type: 'order_created_mto', category: 'order', i18nLabel: 'workflows.triggers.order_created_mto', icon: 'shopping_cart', disabled: false, conditionCategories: ['customer', 'order', 'email'], conditionFields: ['email.subject', 'email.sender_email', 'email.sender_domain'], actionCategories: ['order'] },
    { type: 'order_created_smart_inbox', category: 'order', i18nLabel: 'workflows.triggers.order_created_smart_inbox', icon: 'shopping_cart', disabled: false },
    // Planned for a future version.
    { type: 'user_mentioned_note', category: 'smart_inbox', i18nLabel: 'workflows.triggers.user_mentioned_note', icon: 'alternate_email', disabled: true, conditionCategories: ['email', 'conversation', 'customer'], actionCategories: ['thread', 'task', 'email'] },
  ],
  conditionFields: [
    { key: 'email.subject', i18nLabel: 'workflows.fields.email_subject', category: 'email', disabled: false, dataType: 'string', operators: ['equals', 'not_equals', 'contains', 'contains_any', 'contains_all', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'] },
    { key: 'email.sender_email', i18nLabel: 'workflows.fields.email_sender_email', category: 'email', disabled: false, dataType: 'string', operators: ['equals', 'not_equals', 'contains', 'contains_any', 'contains_all', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'] },
    { key: 'email.sender_domain', i18nLabel: 'workflows.fields.email_sender_domain', category: 'email', disabled: false, dataType: 'string', operators: ['equals', 'not_equals', 'contains', 'contains_any', 'contains_all', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'] },
    { key: 'email.has_attachments', i18nLabel: 'workflows.fields.email_has_attachments', category: 'email', disabled: false, dataType: 'boolean', operators: ['equals'] },
    // Planned for a future version.
    { key: 'email.sentiment', i18nLabel: 'workflows.fields.email_sentiment', category: 'email', disabled: true, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=email.sentiment' },
    // Key kept as email.classification (no migration); grouped and labelled as the conversation Category.
    // Category / Status / Assignee lead the group — the most-used fields appear first, in order.
    { key: 'email.classification', i18nLabel: 'workflows.fields.email_classification', category: 'conversation', disabled: false, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=email.classification' },
    { key: 'task.status', i18nLabel: 'workflows.fields.task_status', category: 'conversation', disabled: false, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=task.status' },
    { key: 'task.assignee', i18nLabel: 'workflows.fields.task_assignee', category: 'conversation', disabled: false, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=task.assignee' },
    { key: 'thread.mailbox', i18nLabel: 'workflows.fields.thread_mailbox', category: 'conversation', disabled: false, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=thread.mailbox' },
    // Planned for a future version.
    { key: 'thread.is_starred', i18nLabel: 'workflows.fields.thread_is_starred', category: 'conversation', disabled: true, dataType: 'boolean', operators: ['equals'] },
    { key: 'thread.is_read', i18nLabel: 'workflows.fields.thread_is_read', category: 'conversation', disabled: false, dataType: 'boolean', operators: ['equals'] },
    // Planned for a future version.
    { key: 'thread.urgency', i18nLabel: 'workflows.fields.thread_urgency', category: 'conversation', disabled: true, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=thread.urgency' },
    { key: 'thread.message_count', i18nLabel: 'workflows.fields.thread_message_count', category: 'conversation', disabled: false, dataType: 'number', operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equals', 'less_than_or_equals'] },
    // Planned for a future version.
    { key: 'task.priority', i18nLabel: 'workflows.fields.task_priority', category: 'conversation', disabled: true, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=task.priority' },
    { key: 'customer.name', i18nLabel: 'workflows.fields.customer_name', category: 'customer', disabled: false, dataType: 'multi_enum', operators: ['in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=customer.name' },
    { key: 'customer.country', i18nLabel: 'workflows.fields.customer_country', category: 'customer', disabled: false, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=customer.country' },
    { key: 'customer.group', i18nLabel: 'workflows.fields.customer_group', category: 'customer', disabled: false, dataType: 'multi_enum', operators: ['in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=customer.group' },
    { key: 'customer.is_known', i18nLabel: 'workflows.fields.customer_is_known', category: 'customer', disabled: false, dataType: 'boolean', operators: ['equals'] },
    { key: 'order.total', i18nLabel: 'workflows.fields.order_total', category: 'order', disabled: false, dataType: 'number', operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equals', 'less_than_or_equals'] },
    { key: 'order.item_count', i18nLabel: 'workflows.fields.order_item_count', category: 'order', disabled: false, dataType: 'number', operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equals', 'less_than_or_equals'] },
    { key: 'order.unmatched_count', i18nLabel: 'workflows.fields.order_unmatched_count', category: 'order', disabled: false, dataType: 'number', operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equals', 'less_than_or_equals'] },
    { key: 'order.ambiguous_count', i18nLabel: 'workflows.fields.order_ambiguous_count', category: 'order', disabled: false, dataType: 'number', operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equals', 'less_than_or_equals'] },
    { key: 'order.status', i18nLabel: 'workflows.fields.order_status', category: 'order', disabled: false, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=order.status' },
    { key: 'order.channel', i18nLabel: 'workflows.fields.order_channel', category: 'order', disabled: true, dataType: 'enum', operators: ['equals', 'not_equals', 'in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=order.channel' },
    { key: 'order.shipping_known', i18nLabel: 'workflows.fields.order_shipping_known', category: 'order', disabled: false, dataType: 'boolean', operators: ['equals'] },
    { key: 'order.has_delivery_date', i18nLabel: 'workflows.fields.order_has_delivery_date', category: 'order', disabled: false, dataType: 'boolean', operators: ['equals'] },
    { key: 'order.has_alerts', i18nLabel: 'workflows.fields.order_has_alerts', category: 'order', disabled: false, dataType: 'boolean', operators: ['equals'] },
    { key: 'order.contains_product', i18nLabel: 'workflows.fields.order_contains_product', category: 'order', disabled: false, dataType: 'multi_enum', operators: ['in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=order.contains_product' },
    { key: 'order.contains_brand', i18nLabel: 'workflows.fields.order_contains_brand', category: 'order', disabled: false, dataType: 'multi_enum', operators: ['in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=order.contains_brand' },
    { key: 'order.contains_category', i18nLabel: 'workflows.fields.order_contains_category', category: 'order', disabled: false, dataType: 'multi_enum', operators: ['in', 'not_in'], enumEndpoint: '/v1/workflow-rules/enum-values?field=order.contains_category' },
  ],
  actions: [
    // Conversation actions first (category → status → assignee → comment),
    // then order actions (set owner → change order status).
    { type: 'change_category', category: 'email', i18nLabel: 'workflows.actions.change_category', icon: 'label', disabled: false, excludedTriggers: ['email_categorized'], params: [{ key: 'classificationId', i18nLabel: 'workflows.params.classification', controlType: 'select', required: true, optionsEndpoint: '/v1/workflow-rules/enum-values?field=email.classification' }] },
    { type: 'change_status', category: 'task', i18nLabel: 'workflows.actions.change_status', icon: 'swap_horiz', disabled: false, excludedTriggers: ['task_status_changed'], params: [{ key: 'statusId', i18nLabel: 'workflows.params.status', controlType: 'select', required: true, optionsEndpoint: '/v1/workflow-rules/enum-values?field=task.status' }] },
    { type: 'assign_user', category: 'thread', i18nLabel: 'workflows.actions.assign_user', icon: 'person', disabled: false, excludedTriggers: ['thread_assigned'], params: [{ key: 'userId', i18nLabel: 'workflows.params.user', controlType: 'select', required: true, optionsEndpoint: '/v1/workflow-rules/enum-values?field=task.assignee' }] },
    { type: 'add_note', category: 'thread', i18nLabel: 'workflows.actions.add_note', icon: 'note_add', disabled: false, params: [{ key: 'content', i18nLabel: 'workflows.params.content', controlType: 'mention_textarea', required: true }] },
    { type: 'set_owner', category: 'order', i18nLabel: 'workflows.actions.set_owner', icon: 'manage_accounts', disabled: false, params: [{ key: 'ownerId', i18nLabel: 'workflows.params.owner', controlType: 'select', required: true, optionsEndpoint: '/v1/workflow-rules/enum-values?field=order.owner' }] },
    { type: 'change_order_status', category: 'order', i18nLabel: 'workflows.actions.change_order_status', icon: 'local_shipping', disabled: false, params: [{ key: 'orderStatusId', i18nLabel: 'workflows.params.order_status', controlType: 'select', required: true, optionsEndpoint: '/v1/workflow-rules/enum-values?field=order.status' }] },
    { type: 'modify_order_details', category: 'order', i18nLabel: 'workflows.actions.modify_order_details', icon: 'edit_note', disabled: false, params: [
      { key: 'modifications', i18nLabel: 'workflows.params.modifications', controlType: 'order_field_modifications', required: true,
        orderFields: [
          { key: 'shipping_date', i18nLabel: 'workflows.params.shipping_date', fieldType: 'date_offset' },
          { key: 'partner_id', i18nLabel: 'workflows.params.location', fieldType: 'location', optionsEndpoint: '/v1/workflow-rules/enum-values?field=order.location' },
          { key: 'comment', i18nLabel: 'workflows.params.internal_note', fieldType: 'textarea' },
        ],
        customFields: [
          { technicalName: 'po_reference', label: 'PO Reference', fieldType: 'text' },
          { technicalName: 'priority_tier', label: 'Priority Tier', fieldType: 'single_select', allowedValues: ['Standard', 'Express', 'Critical'] },
          { technicalName: 'gift_wrap', label: 'Gift Wrap', fieldType: 'boolean' },
        ] },
    ] },
    // Planned for a future version.
    { type: 'create_draft', category: 'email', i18nLabel: 'workflows.actions.create_draft', icon: 'drafts', disabled: true, params: [{ key: 'body', i18nLabel: 'workflows.params.body', controlType: 'textarea', required: false }] },
    // Only offered for new_email_received (fires once per conversation, so a conversation is forwarded exactly once).
    { type: 'forward_email', category: 'email', i18nLabel: 'workflows.actions.forward_email', icon: 'forward', disabled: false, allowedTriggers: ['new_email_received'], params: [{ key: 'recipients', i18nLabel: 'workflows.params.recipients', controlType: 'email_list', required: true }] },
    { type: 'send_reply', category: 'email', i18nLabel: 'workflows.actions.send_reply', icon: 'reply', disabled: false, excludedTriggers: ['order_created_mto'], params: [
      { key: 'body', i18nLabel: 'workflows.params.message', controlType: 'template_textarea', required: true, variables: [
        { key: 'customer.name' },
        { key: 'customer.location' },
        { key: 'order.reference', triggers: ['order_created_smart_inbox'] },
        { key: 'order.total', triggers: ['order_created_smart_inbox'] },
      ] },
    ] },
    // Reachable only from the order-conversation template: enabled so the builder
    // can render a locked row, templateOnly so it never enters the action picker.
    { type: 'create_order', category: 'thread', i18nLabel: 'workflows.actions.create_order', icon: 'add_shopping_cart', disabled: false, templateOnly: true, allowedTriggers: ['new_email_received'], requiredConditions: [{ field: 'email.classification', operator: 'equals', value: 'order' }], params: [] },
    // Planned for a future version.
    { type: 'generate_document', category: 'order', i18nLabel: 'workflows.actions.generate_document', icon: 'description', disabled: true, params: [] },
    // Planned for a future version.
    { type: 'create_task', category: 'task', i18nLabel: 'workflows.actions.create_task', icon: 'add_task', disabled: true, params: [{ key: 'title', i18nLabel: 'workflows.params.title', controlType: 'text', required: true }] },
    // Planned for a future version.
    { type: 'ai_task_creation', category: 'task', i18nLabel: 'workflows.actions.ai_task_creation', icon: 'smart_toy', disabled: true, params: [{ key: 'prompt', i18nLabel: 'workflows.params.prompt', controlType: 'textarea', required: false }] },
    // Planned for a future version.
    { type: 'mark_starred', category: 'thread', i18nLabel: 'workflows.actions.mark_starred', icon: 'star', disabled: true, params: [] },
    // Planned for a future version.
    { type: 'archive_thread', category: 'thread', i18nLabel: 'workflows.actions.archive_thread', icon: 'archive', disabled: true, params: [] },
  ],
};

export const MOCK_WORKFLOW_ENUM_VALUES: Record<string, IEnumOption[]> = {
  'email.classification': [
    { value: 'order', label: 'Order' },
    { value: 'rfq', label: 'RFQ' },
    { value: 'support', label: 'Support' },
    { value: 'inquiry', label: 'Inquiry' },
    { value: 'spam', label: 'Spam' },
    { value: 'undefined', label: 'Undefined' },
  ],
  'email.sentiment': [
    { value: 'positive', label: 'Positive' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'negative', label: 'Negative' },
  ],
  'thread.urgency': [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ],
  'task.status': [
    { value: 'open', label: 'Open' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'closed', label: 'Closed' },
  ],
  'task.priority': [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ],
  'task.assignee': [
    { value: 1, label: 'Alice Martin' },
    { value: 2, label: 'Bob Chen' },
    { value: 3, label: 'Carlos Diaz' },
  ],
  'thread.mailbox': [
    { value: 1, label: 'sales@volta.example' },
    { value: 2, label: 'support@volta.example' },
  ],
  'customer.name': [
    { value: 1, label: 'Acme Corp - Paris Office' },
    { value: 2, label: 'Acme Corp - Lyon Warehouse' },
    { value: 3, label: 'Globex Industries - Milan Showroom' },
    { value: 4, label: 'Initech - Berlin Branch' },
  ],
  'customer.country': [
    { value: 'FR', label: 'France' },
    { value: 'DE', label: 'Germany' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'US', label: 'United States' },
  ],
  'customer.group': [
    { value: 1, label: 'VIP accounts' },
    { value: 2, label: 'Retail partners' },
    { value: 3, label: 'Wholesale buyers' },
    { value: 4, label: 'Key accounts' },
  ],
  'order.status': [
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'to_be_validated', label: 'To Be Validated' },
    { value: 'pending_inventory', label: 'Pending Inventory' },
    { value: 'sale', label: 'Confirmed' },
    { value: 'done', label: 'Done' },
    { value: 'cancel', label: 'Cancelled' },
  ],
  'order.channel': [
    { value: 'email', label: 'Email' },
    { value: 'prompt', label: 'Prompt to Order' },
    { value: 'B2B portal', label: 'B2B Portal' },
    { value: 'BO portal', label: 'BO Portal' },
    { value: 'Agent portal', label: 'Agent Portal' },
  ],
  'order.owner': [
    { value: 1, label: 'Alice Martin' },
    { value: 2, label: 'Bob Chen' },
    { value: 3, label: 'Carlos Diaz' },
  ],
  'order.location': [
    { value: 1, label: 'Acme Corp - Paris Office' },
    { value: 2, label: 'Acme Corp - Lyon Warehouse' },
    { value: 3, label: 'Globex Industries - Milan Showroom' },
    { value: 4, label: 'Initech - Berlin Branch' },
  ],
  'order.contains_brand': [
    { value: 1, label: 'Acme' },
    { value: 2, label: 'Globex' },
    { value: 3, label: 'Initech' },
  ],
  'order.contains_category': [
    { value: 1, label: 'Beverages' },
    { value: 2, label: 'Hardware' },
    { value: 3, label: 'Office Supplies' },
  ],
  'order.contains_product': [
    { value: 1, label: 'SKU-1001 Steel bolt M8' },
    { value: 2, label: 'SKU-1002 Hex nut M8' },
    { value: 3, label: 'SKU-2001 Copper wire 2.5mm' },
  ],
};

const TRIGGERS: WorkflowTriggerType[] = [
  'new_email_received',
  'email_categorized',
  'task_status_changed',
  'thread_assigned',
  'order_created_mto',
  'order_created_smart_inbox',
  'user_mentioned_note',
];

const RULE_NAMES = [
  'Auto-draft reply for RFQs',
  'Assign owner by classification',
  'Star negative sentiment emails',
  'Archive closed tasks',
  'Route invoices to accounting',
  'Escalate VIP customer requests',
];

const RULE_PREVIEWS = [
  'When email categorized → If Classification is one of RFQ → Then Create draft',
  'When new email received → If Sender domain contains acme.com → Then Assign user',
  'When email categorized → If Classification equals Spam → Then Star thread',
  'When task status changed → If Conversation status equals Closed → Then Archive thread',
  'When new email received → If Subject contains invoice → Then Change classification, Send reply',
  'When thread assigned → If Client group is one of VIP accounts → Then Assign user',
];

const HISTORY_SUBJECTS = [
  'PO-2026-0048 - Delivery date confirmation',
  'RFQ for stainless steel fittings',
  'Invoice INV-2026-9911 requires review',
  'Urgent: Update shipping address for order #8821',
  'Question about product compatibility',
  'Follow-up on delayed shipment',
  'Need revised quotation for 10k units',
  'Complaint - wrong item received',
  'Can we schedule a call this afternoon?',
  'Payment proof attached for order SO-2142',
];

function ruleStatusByIndex(index: number): WorkflowRuleStatus {
  if (index % 10 === 0) {
    return 'archived';
  }
  if (index % 4 === 0) {
    return 'paused';
  }
  return 'active';
}

function historyStatusByIndex(index: number): WorkflowExecutionStatus {
  if (index % 17 === 0) {
    return 'chain_limit';
  }
  if (index % 14 === 0) {
    return 'skipped';
  }
  if (index % 9 === 0) {
    return 'partial_failure';
  }
  if (index % 5 === 0) {
    return 'failure';
  }
  return 'success';
}

export function buildMockWorkflowRules(
  count = WORKFLOW_MOCK_CONFIG.rulesCount
): IWorkflowRuleSummary[] {
  return Array.from({ length: count }, (_value, index) => {
    const triggerType = TRIGGERS[index % TRIGGERS.length];
    const status = ruleStatusByIndex(index);
    const now = Date.now();
    const createdAtMs = now - (index + 5) * 86400000;
    const updatedAtMs = createdAtMs + (index % 12) * 3600000;
    const lastRunAt = index % 11 === 0
      ? null
      : new Date(now - (index + 1) * 2 * 3600000).toISOString();

    return {
      id: String(index + 1),
      name: `${RULE_NAMES[index % RULE_NAMES.length]} #${index + 1}`,
      description: index % 3 === 0
        ? null
        : `Automation generated for ${triggerType.replace(/_/g, ' ')}.`,
      preview: RULE_PREVIEWS[index % RULE_PREVIEWS.length],
      status,
      triggerType,
      actionCount: 1 + (index % 3),
      conditionCount: index % 4,
      stopProcessing: index % 2 === 0,
      sequence: (index + 1) * 10,
      timesRun: (index + 1) * 7,
      lastRunAt,
      createdAt: new Date(createdAtMs).toISOString(),
      updatedAt: new Date(updatedAtMs).toISOString(),
    };
  });
}

export function buildMockWorkflowHistory(
  count = WORKFLOW_MOCK_CONFIG.historyCount,
  rules: IWorkflowRuleSummary[] = buildMockWorkflowRules()
): IWorkflowExecution[] {
  const sourceRules = rules.length ? rules : buildMockWorkflowRules(1);

  return Array.from({ length: count }, (_value, index) => {
    const rule = sourceRules[index % sourceRules.length];
    const status = historyStatusByIndex(index);
    const actionsExecuted = 1 + (index % 3);
    const actionsFailed =
      status === 'success' || status === 'skipped' || status === 'chain_limit'
      ? 0
      : 1 + (index % 2);
    const isOrderTrigger =
      rule.triggerType === 'order_created_mto' ||
      rule.triggerType === 'order_created_smart_inbox';
    // MTO orders carry no thread; smart-inbox orders and email automations keep one.
    const threadMissing = index % 13 === 0 || rule.triggerType === 'order_created_mto';

    return {
      id: `exec-${index + 1}`,
      ruleId: rule.id,
      ruleName: rule.name,
      triggerType: rule.triggerType,
      thread: threadMissing
        ? null
        : {
          id: 5000 + index,
          subject: HISTORY_SUBJECTS[index % HISTORY_SUBJECTS.length],
        },
      order: isOrderTrigger
        ? { id: 600 + index, name: `S00${600 + index}` }
        : null,
      status,
      actionsExecuted,
      actionsFailed,
      executedAt: new Date(Date.now() - index * 90 * 1000).toISOString(),
      durationMs: 120 + ((index * 37) % 900),
    };
  });
}

/** Mirrors GET /v1/workflow-rules/templates (see utils/rule_templates.py). */
export const MOCK_WORKFLOW_TEMPLATES: IWorkflowTemplate[] = [
  {
    key: 'order_from_classified_conversation',
    i18nLabel: 'workflows.templates.order_from_classified_conversation.title',
    i18nDescription: 'workflows.templates.order_from_classified_conversation.desc',
    i18nDefaultName: 'workflows.templates.order_from_classified_conversation.default_name',
    icon: 'add_shopping_cart',
    trigger: { type: 'new_email_received', params: {} },
    conditionGroups: [
      {
        id: 'mock-tpl-cg-1',
        match: 'all',
        conditions: [
          {
            id: 'mock-tpl-c-1',
            field: 'email.classification',
            operator: 'equals',
            value: 'order',
          },
        ],
      },
    ],
    actions: [{ id: 'mock-tpl-a-1', type: 'create_order', params: {}, order: 0 }],
  },
];

export const MOCK_WORKFLOW_RULES: IWorkflowRuleSummary[] = buildMockWorkflowRules();
export const MOCK_WORKFLOW_HISTORY: IWorkflowExecution[] = buildMockWorkflowHistory(
  undefined,
  MOCK_WORKFLOW_RULES
);
